import {LTServices} from "../lt-ng/lt-services";
import {L} from "../lt-ng/landcare-toolkit";
import {
    IFeatureState
} from "../services/feature_state";
import {Panes} from "./panes";
import {ISavedFeatureService} from "../services/features-api/saved_features";
import IMapService = LTServices.IMapService;
import getLayersForAreaQuery = L.tree.getLayersForAreaQuery;
import Item = L.tree.Item;
import getVisibleScienceLayers = L.tree.getVisibleScienceLayers;
import getLayerId = L.tree.getLayerId;
import {GeoAppEvents} from "./events/geoapp_events";
import {FeaturesEvents} from "../services/events/FeaturesEvents";
import "silverstripe";
import filterNodes = L.tree.filterNodes;
import isCustomLayerQueryable = L.tree.isCustomLayerQueryable;
import {
    AreaReportFeatureKind,
    FeatureReport,
    ISharedFeatureControlState, ReportKind
} from "../services/ISharedFeatureState";
import {IAppConfig} from "../services/app-config/app_config";
import {_t} from "../utility/i18n";
import getIncludedScienceLayers = L.tree.getIncludedScienceLayers;

type AddFeatureModes = 'pin' | 'extent' | 'drawn' | 'predefined';

export interface IFeaturesScope extends ng.IScope {
    activeScienceLayerName: () => string;
    modeDisabled: (mode: string) => boolean;
    activeModeMenuTitle: () => string;
    predefinedLayerTitle: (item: L.tree.Item) => string;
    modeTitle: () => string;
    layerIsLoading(layer: L.tree.Item): boolean;
    queryAreaFromLayer();
    templateForReport(report : FeatureReport)
    toggleMode(mode: AddFeatureModes, layer?: L.tree.Item);
    isModeDisabledOE(mode: AddFeatureModes): boolean;
    modeButtonClass(mode: AddFeatureModes, layer?: L.tree.Item) : string|object;
    queryableLayers: L.tree.Item[];
    queryableCustomLayers: L.tree.Item[];
    localDataLayers: L.tree.Item[];
    displayPointQuery();
    doDrawnQuery: () => void;
    backToStart();
    layerPicker: {active: boolean};
    toggleLayerPicker(): void;
    sharedState(): ISharedFeatureControlState;
    isPredefinedLayerEnabled (item: L.tree.Item) : boolean;
    menuOpen: boolean;
    mainBtnIcon() : object|string;
    menuClass(): object;
    fabButtonClick();
    deleteFeatureReport();
    saveFeatureReport(): ng.IPromise<void>;
    isReportSaved(): boolean;
    isReportSaving(): boolean;
    saveActiveFeature(columns?: string[], saveEvenIfNew?: boolean);
    showEditTitleModal();
    loadingContent: boolean;
    featureMode: string;
}

export class FeaturesControl {
    public static $inject = [
        '$scope',
        'landcareMapService',
        'featureState',
        'savedFeatureService',
        'appConfig'
    ];

    public constructor(protected $scope: IFeaturesScope,
                       protected mapService: IMapService,
                       protected featureState: IFeatureState,
                       protected savedFeatureService: ISavedFeatureService,
                       protected appConfig: IAppConfig) {
        Object.defineProperty($scope, 'menuOpen', {
            get () {
                return featureState.sharedState().fabMenuOpen || false;
            },
            set (isOpen) {
                featureState.sharedState().fabMenuOpen = isOpen;
            }
        });

        $scope.toggleMode = (mode, layer) => this.featureState.toggleMode(mode, layer);
        $scope.sharedState = () => this.featureState.sharedState();

        // see https://docs.angularjs.org/api/ng/directive/ngInclude#event-$includeContentRequested
        $scope.loadingContent = false;
        $scope.$on('$includeContentRequested', () => $scope.loadingContent = true);
        $scope.$on('$includeContentLoaded', () => $scope.loadingContent = false);
        $scope.$on('$includeContentError', () => $scope.loadingContent = false);

        const LOCATION = 'Location';
        const AREA = 'Custom Area';
        const EXTENT = 'Extent';

        $scope.templateForReport = (report : FeatureReport) => {
            switch (report.kind) {
                case ReportKind.pin:
                    return '/Partial/Pane/Features_PointQuery';
                case ReportKind.multiplePin:
                    return '/Partial/Pane/Features_MultiPointQuery';
                case ReportKind.area:
                    return '/Partial/Pane/Features_AreaQuery';
            }
        };

        $scope.$on(FeaturesEvents.reportChanged, () => {
            if ($scope.menuOpen) {
                $scope.menuOpen = false;
            }
        });

        $scope.$on(Panes.Events.CLOSE_MOBILE_PANE, (ev, pane) => {
            if (pane == Panes.Ids.FEATURES) {
                $scope.backToStart();
            }
        });

        $scope.$on(GeoAppEvents.featureClicked, () => {
            $scope.menuOpen = false;
        });

        $scope.fabButtonClick = () => {
            const fs = this.featureState;
            const state = fs.sharedState();
            if ($scope.menuOpen && state.featureMode) {
                fs.toggleMode(state.featureMode, state.activeQueryLayer);
            }
            $scope.menuOpen = !$scope.menuOpen;
            return;
        };

        $scope.$on(Panes.Events.ACTIVE_CHANGED, (event, pane) => {
            if (pane) {
                $scope.menuOpen = false;
            }
        });

        $scope.menuClass = () => {
            return {
                'is-open' : $scope.menuOpen && !$scope.sharedState().featureMode
            };
        };

        const addFeaturesIcon = window.ss.i18n._t("App.ADD_FEATURES_ICON", 'icon-add-features');

        $scope.mainBtnIcon = () => {
            const anyLoading = $scope.queryableLayers.some(l => $scope.layerIsLoading(l));

            if (anyLoading) {
                return {
                    "icon-spinner": true,
                    "spinning": true
                }
            }

            const result = {
                'icon-close_large': $scope.menuOpen,
            };

            result[addFeaturesIcon] = !$scope.menuOpen;

            return result;

        };

        let modeTitles : {[mode: string] : string} = {
            'pin': 'FEATURES_PANE.DROP_PIN',
            'drawn': 'FEATURES_PANE.DRAW_AREA',
            'extent': 'FEATURES_PANE.USE_EXTENT'
        };

        $scope.modeTitle = () => {
            const state = $scope.sharedState();
            switch (state.featureMode) {
                case 'pin':
                    return LOCATION;
                case 'drawn':
                    return AREA;
                case 'predefined':
                    if (state.activeQueryLayer) return state.activeQueryLayer.name;
                    return AREA;
                case 'extent':
                    return EXTENT;
            }

            let report = $scope.sharedState().activeReport;

            if (report) {
                switch (report.kind) {
                    case ReportKind.pin:
                        return LOCATION;
                    case ReportKind.area:
                        switch (report.featureKind) {
                            case AreaReportFeatureKind.predefined:
                                if (report.sourceLayer && report.sourceLayer.name) {
                                    return report.sourceLayer.name
                                } else {
                                    return AREA
                                }
                            default:
                                return AREA;
                        }
                }
            }

            return AREA;
        };

        $scope.activeModeMenuTitle = () => {
            let state= $scope.sharedState();
            if (modeTitles[state.featureMode]) {
                return _t(modeTitles[state.featureMode]);
            }

            if (state.featureMode == 'predefined') {
                return state.activeQueryLayer.name;
            }

            return AREA;
        };

        $scope.layerPicker = { active: false };

        $scope.$on('closeLayerPicker', () => {
            $scope.layerPicker.active = false
        });

        $scope.toggleLayerPicker = () => {
            if (!getIncludedScienceLayers(this.mapService.treeData).length) {
                $scope.$root.$broadcast(GeoAppEvents.toggleLayerSelector, true, false, true);
                return;
            }

            $scope.layerPicker.active = !$scope.layerPicker.active;
        }

        $scope.doDrawnQuery = () => {
            this.featureState.queryDrawnArea();
        };

        /**
         * Enable a mode where the user clicks on the map to select a geometry from a layer. The geometry is
         * then used in an area query.
         */
        $scope.queryAreaFromLayer = () => {
            this.featureState.queryPredefinedFeatures();
        };


        $scope.layerIsLoading = (layer: L.tree.Item) => {
            return this.featureState.layerIsLoading(layer);
        };

        $scope.modeButtonClass = (mode, layer = null) => {
            let state = $scope.sharedState();
            return {
                'activated': state.featureMode == mode && layer == state.activeQueryLayer
                    || mode === 'pin' && this.appConfig.isSingleClickActive(state)
            };
        };

        $scope.queryableLayers = [];
        $scope.queryableCustomLayers = [];

        let updateQueryable = () => {
            $scope.queryableLayers = getLayersForAreaQuery(mapService.treeData);
            $scope.queryableCustomLayers = filterNodes(mapService.treeData, isCustomLayerQueryable);
            const localData = mapService.treeData.find(n => n.id === 1999);
            $scope.localDataLayers = localData ? localData.children : [];
        };

        // Cover all bases for now to be sure we get it working...
        mapService.promise.then(updateQueryable);
        mapService.mapPromise.then(updateQueryable);
        $scope.$on('updateTreeData', updateQueryable);
        $scope.$on('toggleLayerSelector', updateQueryable);

        $scope.backToStart = () => {
            this.featureState.hideReport();
        };

        $scope.isPredefinedLayerEnabled = (item: Item) => {
            const visibleScienceLayers = getVisibleScienceLayers(this.mapService.treeData)
                .map(getLayerId);

            const cantBeUsedToQuery = item.layerService.cantBeUsedToQueryTheseLayers || [];
            // Is the context layer unsuitable for querying any of the active science layers
            return !cantBeUsedToQuery.some((layerId) => visibleScienceLayers.indexOf(layerId) !== -1);
        };

        $scope.isModeDisabledOE = (mode: AddFeatureModes) => {
            const map = this.mapService.map;
            if (!map) return;
            const view = map.getView();
            const resolution = view.getResolution();

            const visibleLayers = getVisibleScienceLayers(this.mapService.treeData)
            if (!visibleLayers.length) return false;

            if (mode === 'pin') {
                return visibleLayers.some(l => l && l.layerService && !l.layerService.pointQuery);
            }

            const anyVisible = filterNodes(visibleLayers, (node: Item) => {
                    const layer = node && node.layer;
                    if (!layer) return false;

                    return (!layer.getMaxResolution() || resolution <= layer.getMaxResolution())
                        && (!layer.getMinResolution() || resolution >= layer.getMinResolution());

            });

            return anyVisible.length === 0;
        }

        $scope.modeDisabled = (/*mode: string*/) => {
            const visibleScienceLayers = getVisibleScienceLayers(this.mapService.treeData);

            return visibleScienceLayers.some(l => l && l.layerService && !l.layerService.pointQuery);
        }

        $scope.activeScienceLayerName = () => {
            return getVisibleScienceLayers(this.mapService.treeData)
                .map(item => item.name)
                .join(', ');
        }

        function fmtList(ss: string[]) {
            if (ss.length > 1) {
                let head = ss.slice(0, ss.length - 1);
                return head.join(', ') + ' and ' + ss[ss.length[-1]];
            }
            return ss[0] || '';
        }

        $scope.predefinedLayerTitle = (item: Item) => {
            if ($scope.isPredefinedLayerEnabled(item)) {
                return `Report based on ${item.name}`;
            } else {
                let layers = fmtList(getVisibleScienceLayers(mapService.treeData).map((l) => l.name));
                return `${item.name} is not suitable for reporting on ${layers}`;
            }
        };

        $scope.deleteFeatureReport = () => {
            try {
                const rp = this.featureState.sharedState().activeReport;
                if ('userFeatures' in rp) {
                    this.savedFeatureService.deleteFeature(rp.userFeatures);
                }

            } catch (e) {
                console.warn(e);
            }
        };

        $scope.saveFeatureReport = () => {
            return this.featureState.saveActiveReportFeatures();
        };

        $scope.isReportSaved = () => {
            let rp = this.featureState.sharedState().activeReport;
            return rp && 'userFeatures' in rp && rp.userFeatures && rp.userFeatures.userFeature && !!rp.userFeatures.userFeature.id;
        };

        $scope.isReportSaving = () => {
            let rp = this.featureState.sharedState().activeReport;
            return rp && 'userFeatures' in rp && rp.userFeatures && rp.userFeatures.saving;
        };

        $scope.saveActiveFeature = (columns: string[] = null, saveEvenIfNew: boolean = false) => {
            const rp = featureState.getActiveReportIfArea() || featureState.getActiveReportIfPoint();
            const feature = rp && rp.userFeatures;

            if (feature && feature.userFeature && feature.userFeature.id) {
                this.savedFeatureService.saveFeature(feature, columns);
            } else if (saveEvenIfNew) {
                this.featureState.saveActiveReportFeatures();
            }
        };

        $scope.showEditTitleModal = () => {
            $scope.$emit('showModal', '/Partial/modal/EditReportTitle', false, true);
        };
    }
}
